import React, {useState, useEffect, useRef, useCallback} from 'react'
import {Input, FormGroup, Label} from 'reactstrap'
import {removeNode, updateNode, updateText} from '../../../editorActions'
import {ReactComponent as DragIcon} from '../../../../../assets/icons/drag.svg'
import {ReactComponent as LockIcon} from '../../../../../assets/icons/lock.svg'
import {ReactComponent as LockOpenIcon} from '../../../../../assets/icons/lock_open.svg'
import {ReactComponent as TrashIcon} from '../../../../../assets/icons/trash-alt.svg'
import {ReactComponent as TextThumbIcon} from '../../../../../assets/icons/text-thumb.svg'
import {ReactComponent as ImageThumbIcon} from '../../../../../assets/icons/image-thumb.svg'

function NodeToolboxHeader({node, dispatch, onRemove, onLockToggle}) {
    const isText = node.type === 'text'
    const title = isText ? node.content?.text || node.style?.text || 'Text' : 'Image'
    const subtitle = isText ? 'Text layer' : 'DPI 72'
    const locked = !!node.state?.locked
    const [imageError, setImageError] = React.useState(false)
    const imageSrc = node.content?.src ?? node.style?.src
    const showImagePreview = !isText && imageSrc && !imageError

    const handleRemove = (e) => {
        e.stopPropagation()
        onRemove(node.id)
    }

    const handleLock = (e) => {
        e.stopPropagation()
        onLockToggle(node.id, !locked)
    }

    return (
        <div className="SidebarPanel__NodeToolboxHeader">
            <div className="SidebarPanel__LayerThumbnail">
                {isText ? (
                    <TextThumbIcon width={24} height={24} />
                ) : showImagePreview ? (
                    <img
                        src={imageSrc}
                        alt=""
                        onError={() => setImageError(true)}
                        style={{width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit'}}
                    />
                ) : (
                    <ImageThumbIcon width={24} height={24} />
                )}
            </div>
            <div className="SidebarPanel__LayerMeta">
                <div className="SidebarPanel__LayerName">{title}</div>
                <div className={`SidebarPanel__LayerSubtitle ${!isText ? 'SidebarPanel__LayerSubtitle--image' : ''}`}>
                    {subtitle}
                </div>
            </div>
            <div className="SidebarPanel__LayerActions">
                <span className="SidebarPanel__LayerAction" aria-label="Reorder">
                    <DragIcon width={16} height={16} />
                </span>
                <button
                    type="button"
                    className="SidebarPanel__LayerAction"
                    onClick={handleLock}
                    aria-label={locked ? 'Unlock' : 'Lock'}
                >
                    {locked ? <LockIcon width={16} height={16} /> : <LockOpenIcon width={16} height={16} />}
                </button>
                <button type="button" className="SidebarPanel__LayerAction" onClick={handleRemove} aria-label="Delete">
                    <TrashIcon width={16} height={16} />
                </button>
            </div>
        </div>
    )
}

function InputWithUnit({label, value, unit, onChange}) {
    const handleChange = (e) => {
        const v = e.target.value
        const num = v === '' || v === '-' ? '' : parseFloat(v)
        if (num !== '' && Number.isNaN(num)) return
        onChange(num === '' ? v : num)
    }

    const displayValue = value === undefined || value === null ? '' : String(value)

    return (
        <div className="SidebarPanel__NodeInputRow">
            <label className="SidebarPanel__NodeInputLabel">{label}</label>
            <div className="SidebarPanel__NodeInputWrap">
                <input
                    type="text"
                    className="SidebarPanel__NodeInput"
                    value={displayValue}
                    onChange={handleChange}
                    aria-label={label}
                />
                <span className="SidebarPanel__NodeInputUnit">{unit}</span>
            </div>
        </div>
    )
}

const STROKE_WIDTH_MIN = 0
const STROKE_WIDTH_MAX = 50

function StrokeSilhouetteSection({node, dispatch}) {
    const style = node.style || {}
    const strokeWidth = style.strokeWidth ?? 0

    const updateStyle = (patch) => {
        dispatch(updateNode(node.id, {style: {...style, ...patch}}))
    }

    return (
        <div className="SidebarPanel__NodeToolboxSection">
            <div className="SidebarPanel__SectionLabel">Stroke (Silhouette only)</div>
            <FormGroup className="SidebarPanel__StrokeRow">
                <Label for="stroke-width-slider" className="SidebarPanel__StrokeSliderLabel">
                    Width: {strokeWidth} px
                </Label>
                <Input
                    id="stroke-width-slider"
                    type="range"
                    min={STROKE_WIDTH_MIN}
                    max={STROKE_WIDTH_MAX}
                    value={strokeWidth}
                    onChange={(e) => updateStyle({strokeWidth: Number(e.target.value)})}
                    className="SidebarPanel__StrokeSlider"
                    aria-label="Stroke width"
                />
            </FormGroup>
            <div className="SidebarPanel__SectionNote">Black stroke only. Visible in Silhouette view.</div>
        </div>
    )
}

function NodeToolboxText({node, dispatch}) {
    const geo = node.geometry || {}
    const style = node.style || {}
    const currentText = node.content?.text ?? node.style?.text ?? ''

    const [localText, setLocalText] = useState(currentText)
    const debounceRef = useRef(null)
    const lastNodeIdRef = useRef(node.id)

    useEffect(() => {
        if (lastNodeIdRef.current !== node.id) {
            lastNodeIdRef.current = node.id
            setLocalText(node.content?.text ?? node.style?.text ?? '')
            return
        }
        setLocalText(node.content?.text ?? node.style?.text ?? '')
    }, [node.id, node.content?.text, node.style?.text])

    const flushText = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
    }, [])

    const dispatchText = useCallback(
        (text) => {
            if (text.trim() === '') return
            dispatch(updateText(node.id, text, {recordHistory: true}))
        },
        [dispatch, node.id],
    )

    const handleTextChange = (e) => {
        const val = e.target.value
        setLocalText(val)
        flushText()
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null
            dispatchText(val)
        }, 300)
    }

    const handleTextBlur = () => {
        flushText()
        dispatchText(localText)
    }

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const updateGeometry = (patch) => {
        dispatch(updateNode(node.id, {geometry: {...geo, ...patch}}))
    }

    const updateStyle = (patch) => {
        dispatch(updateNode(node.id, {style: {...style, ...patch}}))
    }

    const rotation = geo.rotation ?? 0
    const fontSize = style.fontSize ?? 60
    const x = geo.x ?? 0
    const y = geo.y ?? 0

    return (
        <div className="SidebarPanel__NodeToolboxBody">
            <div className="SidebarPanel__NodeInputRow">
                <label className="SidebarPanel__NodeInputLabel">Text</label>
                <textarea
                    className="SidebarPanel__NodeInput"
                    value={localText}
                    onChange={handleTextChange}
                    onBlur={handleTextBlur}
                    rows={2}
                    style={{resize: 'vertical', width: '100%'}}
                    aria-label="Text content"
                />
            </div>
            <button type="button" className="SidebarPanel__NodeToolboxAddFont">
                + Add font
            </button>
            <div className="SidebarPanel__NodeToolboxFontCase">
                <button type="button" className="SidebarPanel__NodeToolboxFontCaseBtn">
                    −
                </button>
                <button type="button" className="SidebarPanel__NodeToolboxFontCaseBtn">
                    AA
                </button>
                <button type="button" className="SidebarPanel__NodeToolboxFontCaseBtn">
                    Aa
                </button>
                <button type="button" className="SidebarPanel__NodeToolboxFontCaseBtn">
                    aa
                </button>
            </div>
            <div className="SidebarPanel__NodeToolboxGrid">
                <InputWithUnit
                    label="Rotate"
                    value={rotation}
                    unit="deg"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({rotation: Number(v)})}
                />
                <InputWithUnit
                    label="Font size"
                    value={fontSize}
                    unit="%"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateStyle({fontSize: Number(v)})}
                />
                <InputWithUnit
                    label="Position left"
                    value={x}
                    unit="%"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({x: Number(v)})}
                />
                <InputWithUnit
                    label="Position top"
                    value={y}
                    unit="%"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({y: Number(v)})}
                />
            </div>
            <StrokeSilhouetteSection node={node} dispatch={dispatch} />
        </div>
    )
}

function NodeToolboxImage({node, dispatch, onRemoveBackground}) {
    const geo = node.geometry || {}

    const updateGeometry = (patch) => {
        dispatch(updateNode(node.id, {geometry: {...geo, ...patch}}))
    }

    const width = geo.width ?? 100
    const height = geo.height ?? 100
    const rotation = geo.rotation ?? 0
    const x = geo.x ?? 0
    const y = geo.y ?? 0

    return (
        <div className="SidebarPanel__NodeToolboxBody">
            {onRemoveBackground && (
                <div className="SidebarPanel__NodeToolboxActions">
                    <button
                        type="button"
                        className="SidebarPanel__NodeToolboxBtn SidebarPanel__NodeToolboxBtn--removeBg"
                        onClick={() => onRemoveBackground(node)}
                    >
                        Remove Background
                    </button>
                </div>
            )}
            <div className="SidebarPanel__NodeToolboxGrid">
                <InputWithUnit
                    label="Width"
                    value={width}
                    unit="W"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({width: Number(v)})}
                />
                <InputWithUnit
                    label="Height"
                    value={height}
                    unit="H"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({height: Number(v)})}
                />
                <InputWithUnit
                    label="Rotate"
                    value={rotation}
                    unit="deg"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({rotation: Number(v)})}
                />
                <InputWithUnit
                    label="Position left"
                    value={x}
                    unit="%"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({x: Number(v)})}
                />
                <InputWithUnit
                    label="Position top"
                    value={y}
                    unit="%"
                    onChange={(v) => v !== '' && !Number.isNaN(Number(v)) && updateGeometry({y: Number(v)})}
                />
            </div>
            <StrokeSilhouetteSection node={node} dispatch={dispatch} />
        </div>
    )
}

function NodePropertiesToolbox({node, dispatch, inline, onRemoveBackground}) {
    const isText = node.type === 'text'

    const handleRemove = (nodeId) => {
        dispatch(removeNode(nodeId))
    }

    const handleLockToggle = (nodeId, locked) => {
        dispatch(updateNode(nodeId, {state: {locked}}))
    }

    const bodyContent = isText ? (
        <NodeToolboxText node={node} dispatch={dispatch} />
    ) : (
        <NodeToolboxImage node={node} dispatch={dispatch} onRemoveBackground={onRemoveBackground} />
    )

    return (
        <div className={`SidebarPanel__NodeToolbox${inline ? ' is-inline' : ''}`}>
            {!inline && (
                <NodeToolboxHeader
                    node={node}
                    dispatch={dispatch}
                    onRemove={handleRemove}
                    onLockToggle={handleLockToggle}
                />
            )}
            {bodyContent}
        </div>
    )
}

export default NodePropertiesToolbox
